import { useEffect, useState, useCallback, useMemo } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Shield, Stethoscope, Bell, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { ProgressNotesQueue, ProgressNoteReviewPage } from "@/components/ProgressNotes";
import { AuditLog } from "@/components/AuditLog";
import RenewalPage from "@/pages/RenewalPage";
import ReferralPage from "@/pages/ReferralPage";
import { API, formatApiError, useAuth, AuthProvider, ProtectedRoute } from "@/lib/shared";
import { LoginPage } from "@/pages/LoginPage";
import { StaffManagement } from "@/components/StaffManagement";
import { PatientManagement } from "@/components/PatientManagement";
import { ServiceManagement } from "@/components/ServiceManagement";
import { UserManagement } from "@/components/UserManagement";
import { Dashboard } from "@/components/Dashboard";
import { VisitManagement } from "@/components/VisitManagement";
import { FollowUpManagement } from "@/components/FollowUpManagement";
import { EducationLibrary } from "@/components/EducationLibrary";
import { HipaaDisclosure } from "@/components/HipaaDisclosure";
import { DaySchedule } from "@/components/DaySchedule";
import { BigCalendar } from "@/components/BigCalendar";
import { AppointmentModal } from "@/components/AppointmentModal";
import { WebsiteBookingsBanner, BookingRejectionsBanner } from "@/components/Banners";
import { PublicIntakeForm } from "@/pages/PublicIntakeForm";
import ConsentSignPage from "@/pages/ConsentSignPage";
import { NavBar } from "@/components/NavBar";
import { Cancellations } from "@/components/Cancellations";
import { IntakeForms } from "@/components/IntakeForms";
import "@/App.css";

const NotificationsBell = ({ stats, onJump }) => {
  const [open, setOpen] = useState(false);
  const count = (stats.pending_followups || 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" data-testid="notifications-bell" className="relative border-border/60">
          <Bell className="w-4 h-4" />
          {count > 0 && <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">{count > 9 ? '9+' : count}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72">
        <p className="font-semibold text-primary mb-2">Notifications</p>
        <div className="space-y-2 text-sm">
          {count === 0 && <p className="text-muted-foreground py-2">No pending follow-ups · {stats.upcoming_week || 0} appointments this week</p>}
          {count > 0 && (
            <button type="button" onClick={() => { setOpen(false); onJump('followups'); }} className="w-full text-left p-2 rounded-md hover:bg-primary/10 transition-colors">
              <p className="font-medium">{count} pending follow-up{count > 1 ? 's' : ''}</p>
              <p className="text-xs text-muted-foreground">Click to view and complete</p>
            </button>
          )}
          {(stats.upcoming_week || 0) > 0 && (
            <div className="p-2 text-xs text-muted-foreground border-t border-border/40 pt-3">
              📅 {stats.upcoming_week} appointment{stats.upcoming_week > 1 ? 's' : ''} scheduled this week
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// ==================== DAY SCHEDULE (Time slots + Check-in / Check-out) ====================
const MainPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [rebookSource, setRebookSource] = useState(null);
  const [auditCount, setAuditCount] = useState(null);
  const [stats, setStats] = useState({ total_patients: 0, visits_today: 0, pending_followups: 0 });
  const [autoOpenVisit, setAutoOpenVisit] = useState(null);
  const [apptSearch, setApptSearch] = useState("");
  const [calendarFocusDate, setCalendarFocusDate] = useState(null);
  const [focusPatientId, setFocusPatientId] = useState(null);

  const isPhysician = user?.role === 'physician';
  useEffect(() => { if (isPhysician) setActiveTab('progressnotes'); }, [isPhysician]);

  const filteredAppointments = useMemo(() => {
    const q = apptSearch.trim().toLowerCase();
    if (!q) return [...appointments].sort((a, b) => new Date(b.start_datetime) - new Date(a.start_datetime)).slice(0, 50);
    return appointments.filter(apt => {
      const p = patients.find(x => x.id === apt.patient_id);
      const svc = serviceTypes.find(x => x.id === apt.service_type_id);
      const name = p ? `${p.first_name} ${p.last_name}`.toLowerCase() : '';
      return name.includes(q) || svc?.name?.toLowerCase().includes(q) || apt.start_datetime?.toLowerCase().includes(q);
    });
  }, [apptSearch, appointments, patients, serviceTypes]);

  const exportAppointments = async () => {
    try {
      const res = await axios.get(`${API}/export/appointments`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `appointments-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Appointments exported');
    } catch (err) { toast.error('Export failed'); }
  };

  const [rejections, setRejections] = useState([]);

  const fetchRejections = useCallback(async () => {
    // Admins only — endpoint returns 403 for scoped users.
    if (!user || user.role !== 'admin') return;
    try { const res = await axios.get(`${API}/booking-rejections`); setRejections(res.data); }
    catch (_) { /* silent — non-critical banner */ }
    try { const s = await axios.get(`${API}/audit/summary`); setAuditCount(s.data.total_events); } catch (_) { /* silent */ }
  }, [user]);

  const fetchData = useCallback(async () => {
    try {
      await axios.post(`${API}/seed`).catch(() => {});
      const [patientsRes, staffRes, serviceTypesRes, appointmentsRes, statsRes] = await Promise.all([
        axios.get(`${API}/patients`), axios.get(`${API}/staff`), axios.get(`${API}/service-types`), axios.get(`${API}/appointments`), axios.get(`${API}/dashboard/stats`)
      ]);
      setPatients(patientsRes.data); setStaff(staffRes.data); setServiceTypes(serviceTypesRes.data); setAppointments(appointmentsRes.data); setStats(statsRes.data);
      fetchRejections();
    } catch (err) { toast.error("Failed to load data"); }
  }, [fetchRejections]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // Poll rejections every 60s so a fresh silent-drop shows up without a full refresh
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    const t = setInterval(fetchRejections, 60000);
    return () => clearInterval(t);
  }, [user, fetchRejections]);

  const handleLogout = async () => { await logout(); navigate('/login'); };
  const openNewAppointment = () => { setSelectedAppointment(null); setModalOpen(true); };
  const handleDateSelect = (date) => { if (date) { setSelectedDate(date); } };

  const handleJump = (tab, action) => {
    setActiveTab(tab);
    if (tab === 'visits' && action === 'new') setAutoOpenVisit('new');
    if (tab === 'patients' && action?.patientId) setFocusPatientId(action.patientId);
  };

  const handleRebook = (cancelled) => {
    setRebookSource(cancelled);
    setSelectedAppointment(null);
    setModalOpen(true);
  };

  const handleSaveAppointment = async (data) => {
    if (selectedAppointment) { await axios.put(`${API}/appointments/${selectedAppointment.id}`, data); toast.success('Updated'); }
    else if (data.frequency) { const r = await axios.post(`${API}/appointments/series`, data); toast.success(`Booked ${r.data.count} repeating visits`); }
    else {
      const r = await axios.post(`${API}/appointments`, data);
      if (rebookSource) { await axios.post(`${API}/appointments/${rebookSource.id}/rebooked`, { new_appointment_id: r.data.id }).catch(() => {}); setRebookSource(null); toast.success('Rebooked!'); }
      else toast.success('Booked!');
    }
    fetchData();
  };

  const handleDeleteAppointment = async (id) => { await axios.delete(`${API}/appointments/${id}`); toast.success('Deleted'); fetchData(); };

  const handleCalendarCheckIn = async (apt) => {
    try {
      await axios.post(`${API}/appointments/${apt.id}/check-in`);
      const p = patients.find(x => x.id === apt.patient_id);
      toast.success(`Checked in: ${p?.first_name || 'patient'}`);
      fetchData();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || 'Check-in failed'); }
  };

  const handleCalendarViewPatient = (p) => {
    if (!p) return;
    setFocusPatientId(p.id);
    setActiveTab('patients');
  };

  return (
    <div className="min-h-screen" data-testid="main-page">
      {/* Subtle ambient glow */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] rounded-full bg-amber-500/[0.04] blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] rounded-full bg-neutral-400/[0.03] blur-3xl" />
      </div>
      <div className="relative z-10">
      {/* Header */}
      <header className="border-b border-primary/20 bg-card/60 backdrop-blur-xl px-6 py-3 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/eliteiv-logo.png" alt="EliteIV Lounge" className="w-12 h-12 object-contain drop-shadow-[0_0_8px_rgba(212,175,55,0.4)]" />
            <div className="flex flex-col leading-tight">
              <h1 className="text-2xl font-bold brand-heading">
                <span className="gold-gradient-text">Elite</span><span className="silver-gradient-text">IV</span>
              </h1>
              <span className="text-[10px] text-muted-foreground tracking-[0.25em] uppercase">EMR System</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <NotificationsBell stats={stats} onJump={handleJump} />
            <div className="flex items-center gap-2 pr-3 border-r border-border/50">
              <Avatar className="h-8 w-8 ring-1 ring-primary/40"><AvatarFallback className="bg-primary/15 text-primary font-semibold">{user?.name?.[0]}</AvatarFallback></Avatar>
              <span className="text-sm font-medium text-silver">{user?.name}</span>
            </div>
            {user?.role === 'admin' && <Button variant="outline" size="sm" onClick={() => setActiveTab("users")} data-testid="admin-btn" className="border-primary/40 text-primary hover:bg-primary/10"><Shield className="w-4 h-4 mr-1" />Admin</Button>}
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn" className="border-border/60 hover:bg-destructive/10 hover:text-destructive hover:border-destructive/40"><LogOut className="w-4 h-4 mr-1" />Logout</Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto p-6">
        {user && user.role !== 'admin' && user.staff_id && (
          <div
            className="mb-6 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 flex items-center gap-3"
            data-testid="nurse-scope-banner"
          >
            <div className="shrink-0 rounded-full bg-primary/15 p-2 ring-1 ring-primary/40">
              <Stethoscope className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary">My Schedule</p>
              <p className="text-sm text-foreground/90 mt-0.5">
                You&apos;re viewing only patients and appointments assigned to you. Ask an admin if you need broader access.
              </p>
            </div>
          </div>
        )}
        <WebsiteBookingsBanner
          appointments={appointments}
          patients={patients}
          serviceTypes={serviceTypes}
          staff={staff}
          onRefresh={fetchData}
          onJumpToDate={(d) => {
            setCalendarFocusDate(new Date(d));
            setActiveTab('calendar');
          }}
        />
        {user && user.role === 'admin' && (
          <BookingRejectionsBanner rejections={rejections} onRefresh={fetchRejections} />
        )}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <NavBar activeTab={activeTab} onSelect={setActiveTab} role={user?.role} auditCount={auditCount} cancelledCount={appointments.filter(a => a.status === 'cancelled' && !a.rebooked_appointment_id).length} />

          <TabsContent value="cancellations">
            <Cancellations appointments={appointments} patients={patients} serviceTypes={serviceTypes} onRefresh={fetchData} onRebook={handleRebook} />
          </TabsContent>
          <TabsContent value="intake">
            <IntakeForms appointments={appointments} patients={patients} onNewPatient={() => handleJump('patients', 'new')} onOpenPatient={(pid) => handleJump('patients', { patientId: pid })} />
          </TabsContent>

          <TabsContent value="dashboard">
            <Dashboard stats={stats} onJump={handleJump} />
          </TabsContent>

          <TabsContent value="calendar">
            <div className="space-y-4">
              <BigCalendar
                appointments={appointments}
                patients={patients}
                staff={staff}
                serviceTypes={serviceTypes}
                onRefresh={fetchData}
                onEditAppointment={(apt) => { setSelectedAppointment(apt); setModalOpen(true); }}
                onCheckIn={handleCalendarCheckIn}
                onViewPatient={handleCalendarViewPatient}
                focusDate={calendarFocusDate}
                onDateChange={handleDateSelect}
              />

              {/* Day schedule with check-in/check-out */}
              <DaySchedule
                appointments={appointments}
                patients={patients}
                staff={staff}
                serviceTypes={serviceTypes}
                selectedDate={selectedDate}
                onRefresh={fetchData}
              />

              {/* Appointment search + list */}
              <Card className="w-full gold-border bg-card/80">
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-sm font-semibold text-primary">All Appointments</h3>
                    <Button variant="outline" size="sm" onClick={exportAppointments} data-testid="export-appointments-btn" className="border-primary/40 text-primary hover:bg-primary/10"><Download className="w-4 h-4 mr-1" />Export CSV</Button>
                  </div>
                  <Input placeholder="Search by patient, service, or date (YYYY-MM-DD)..." value={apptSearch} onChange={(e) => setApptSearch(e.target.value)} className="text-sm" data-testid="appt-search" />
                  <div className="max-h-[280px] overflow-y-auto space-y-1.5">
                    {filteredAppointments.length === 0 && <p className="text-xs text-muted-foreground py-4 text-center">No appointments {apptSearch ? 'match your search' : 'yet'}</p>}
                    {filteredAppointments.map(apt => {
                      const p = patients.find(x => x.id === apt.patient_id);
                      const svc = serviceTypes.find(x => x.id === apt.service_type_id);
                      const dt = new Date(apt.start_datetime);
                      return (
                        <button key={apt.id} type="button" onClick={() => { setSelectedAppointment(apt); setModalOpen(true); }}
                          className="w-full text-left p-2.5 rounded-lg bg-muted/30 hover:bg-primary/10 border border-transparent hover:border-primary/40 transition-all flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{p ? `${p.first_name} ${p.last_name}` : 'Unknown patient'}</p>
                            <p className="text-xs text-muted-foreground truncate">{svc?.name || 'Service'} · {dt.toLocaleDateString()} {dt.toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'})}</p>
                          </div>
                          <Badge variant="outline" className="text-[10px] border-primary/30 text-primary">{apt.status || 'scheduled'}</Badge>
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="patients">
            <Card className="gold-border bg-card/80"><CardContent className="p-6"><PatientManagement patients={patients} onRefresh={fetchData} focusPatientId={focusPatientId} onFocusConsumed={() => setFocusPatientId(null)} /></CardContent></Card>
          </TabsContent>

          <TabsContent value="visits">
            <Card className="gold-border bg-card/80"><CardContent className="p-6"><VisitManagement patients={patients} staff={staff} serviceTypes={serviceTypes} appointments={appointments} onAppointmentsRefresh={fetchData} autoOpen={autoOpenVisit} onAutoOpenConsumed={() => setAutoOpenVisit(null)} /></CardContent></Card>
          </TabsContent>

          <TabsContent value="followups">
            <Card className="gold-border bg-card/80"><CardContent className="p-6"><FollowUpManagement patients={patients} /></CardContent></Card>
          </TabsContent>

          <TabsContent value="staff">
            <Card className="gold-border bg-card/80"><CardContent className="p-6"><StaffManagement staff={staff} onRefresh={fetchData} /></CardContent></Card>
          </TabsContent>

          <TabsContent value="services">
            <Card className="gold-border bg-card/80"><CardContent className="p-6"><ServiceManagement serviceTypes={serviceTypes} onRefresh={fetchData} /></CardContent></Card>
          </TabsContent>

          <TabsContent value="education">
            <Card className="gold-border bg-card/80"><CardContent className="p-6"><EducationLibrary patients={patients} /></CardContent></Card>
          </TabsContent>

          <TabsContent value="hipaa">
            <Card className="gold-border bg-card/80"><CardContent className="p-6"><HipaaDisclosure patients={patients} /></CardContent></Card>
          </TabsContent>

          {user?.role === 'admin' && (
            <TabsContent value="users">
              <Card className="gold-border bg-card/80"><CardContent className="p-6"><UserManagement /></CardContent></Card>
            </TabsContent>
          )}

          {user?.role === 'admin' && (
            <TabsContent value="audit">
              <Card className="gold-border bg-card/80"><CardContent className="p-6"><AuditLog /></CardContent></Card>
            </TabsContent>
          )}

          <TabsContent value="progressnotes">
            <Card className="gold-border bg-card/80"><CardContent className="p-6"><ProgressNotesQueue patients={patients} /></CardContent></Card>
          </TabsContent>
        </Tabs>
      </main>

      <AppointmentModal isOpen={modalOpen} onClose={() => { setModalOpen(false); setRebookSource(null); }} appointment={selectedAppointment} patients={patients} staff={staff} serviceTypes={serviceTypes} onSave={handleSaveAppointment} onDelete={handleDeleteAppointment} onRefreshAfterSeries={fetchData} initialDate={selectedDate} prefill={rebookSource} />
      <Toaster richColors position="top-right" />
      </div>
    </div>
  );
};

// ==================== PUBLIC PATIENT INTAKE FORM ====================
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/intake/:patientId" element={<PublicIntakeForm />} />
          <Route path="/consent/:patientId" element={<ConsentSignPage />} />
          <Route path="/renew/:token" element={<RenewalPage />} />
          <Route path="/refer/:code" element={<ReferralPage />} />
          <Route path="/progress-review/:token" element={<ProtectedRoute><ProgressNoteReviewPage /></ProtectedRoute>} />
          <Route path="/" element={<ProtectedRoute><MainPage /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
  );
}

export default App;
